// src/pages/school/Exam/TemplateQuestions.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, Row, Col, Button, Form, Table, Badge,
  Spinner, Alert, Dropdown, ButtonGroup, Modal, Toast, ToastContainer
} from "react-bootstrap";
import {
  collection, doc, getDoc, onSnapshot, addDoc, setDoc, serverTimestamp,
  query, where, orderBy, limit, deleteDoc
} from "firebase/firestore";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JoditEditor from "jodit-react";

import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";

/* ---------- Constants ---------- */
const DIFFICULTY = ["Easy", "Medium", "Hard"];
const TYPE_OPTIONS = [
  { value: "mcq", label: "MCQ (Single Correct)" },
  { value: "multi_correct", label: "MCQ (Multi Correct)" },
  { value: "true_false", label: "True / False" },
  { value: "descriptive", label: "Descriptive" },
  { value: "match", label: "Match the Following" },
  { value: "assertion_reason", label: "Assertion & Reason" },
  { value: "statement_based", label: "Statement Based" },
  { value: "case_based", label: "Case Based" },
  { value: "mcq_descriptive", label: "MCQ + Descriptive" },
];

const emptyOptionSet = () => ([
  { key: "A", value: "", isCorrect: false },
  { key: "B", value: "", isCorrect: false },
  { key: "C", value: "", isCorrect: false },
  { key: "D", value: "", isCorrect: false },
]);
const emptyStatementRow = (id = crypto.randomUUID()) => ({ id, key: "", html: "", isTrue: false });
const emptyCaseSubQ = (id = crypto.randomUUID()) => ({
  id,
  questionHtml: "",
  options: emptyOptionSet(),
  correctKey: "A",
});

function normalizeType(raw) {
  const s = String(raw || "").trim().toLowerCase().replace(/\s+/g, "_").replace(/[&/]+/g, "_");
  if (["mcq", "single_correct"].includes(s)) return "mcq";
  if (["multi_correct", "mcq_multi", "multiple_correct"].includes(s)) return "multi_correct";
  if (["true_false", "truefalse", "tf"].includes(s)) return "true_false";
  if (["desc", "descriptive"].includes(s)) return "descriptive";
  if (["match", "match_the_following", "mtf"].includes(s)) return "match";
  if (["assertion_reason", "assertion_and_reason", "asser_reason", "assertion_reasoning"].includes(s)) return "assertion_reason";
  if (["statement_based", "statement"].includes(s)) return "statement_based";
  if (["case_based", "case"].includes(s)) return "case_based";
  if (["mcq_descriptive", "mcq+descriptive", "mcq_desc"].includes(s)) return "mcq_descriptive";
  return "mcq";
}

/* ---------- Label helpers for Match ---------- */
const leftLabel = (idx) => {
  let n = idx + 1, s = "";
  while (n > 0) { n--; s = String.fromCharCode(97 + (n % 26)) + s; n = Math.floor(n / 26); }
  return s; // a, b, c ...
};
const toRoman = (num) => {
  const map = [
    [1000, "m"], [900, "cm"], [500, "d"], [400, "cd"], [100, "c"],
    [90, "xc"], [50, "l"], [40, "xl"], [10, "x"], [9, "ix"], [5, "v"], [4, "iv"], [1, "i"]
  ];
  let res = "", n = Math.max(1, Math.floor(num));
  for (const [v, s] of map) while (n >= v) { res += s; n -= v; }
  return res; // i, ii, iii ...
};
const rightLabel = (idx) => toRoman(idx + 1);
const shuffle = (n) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
};
const formatOptionText = (arr = []) =>
  (!arr || !arr.length || arr.some(v => v == null))
    ? "— generate —"
    : arr.map((rIdx, lIdx) => `(${leftLabel(lIdx)})-(${rightLabel(rIdx)})`).join(", ");
const buildMatchOptions = (n) => {
  const opt1 = Array.from({ length: n }, (_, i) => i); // sequential (a-i, b-ii,...)
  const opts = [opt1];
  const seen = new Set([JSON.stringify(opt1)]);
  while (opts.length < 4) {
    let p = shuffle(n), guard = 0;
    while (seen.has(JSON.stringify(p)) && guard < 20) { p = shuffle(n); guard++; }
    opts.push(p); seen.add(JSON.stringify(p));
  }
  return opts;
};
// put near other helpers
const sanitizeQuestionForWrite = (p) => {
  const out = { ...p };

  // options: keep only key/value/isCorrect
  if (out.options) {
    out.options = out.options.map(o => ({
      key: o.key,
      value: o.value || "",
      isCorrect: !!o.isCorrect,
    }));
  }

  // case-based: remove client-side "id" and sanitize options
  if (out.caseSubQs) {
    out.caseSubQs = out.caseSubQs.map(sq => ({
      // DO NOT persist sq.id
      questionHtml: sq.questionHtml || "",
      correctKey: ["A","B","C","D"].includes(sq.correctKey) ? sq.correctKey : "A",
      options: (sq.options || []).map(o => ({
        key: o.key,
        value: o.value || "",
        isCorrect: !!o.isCorrect,
      })),
    }));
  }

  // statements: keep only key/html/isTrue
  if (out.statements) {
    out.statements = out.statements.map(s => ({
      key: s.key || "",
      html: s.html || "",
      isTrue: !!s.isTrue,
    }));
  }

  delete out.id; // guard against any accidental doc-level id
  return out;
};

export default function TemplateQuestions() {
  const { id: templateId } = useParams();
  const nav = useNavigate();
  const { schoolId, school, isActive: hookIsActive, loading: schoolLoading } = useSchool();

  /* ---------- Template + live questions ---------- */
  const [template, setTemplate] = useState(null);
  const [tLoading, setTLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(true);

  // index fallback flags
  const [usingIndexFallback, setUsingIndexFallback] = useState(false);

  // for load/update toasts
  const hasLoadedRef = useRef(false);
  const prevLenRef = useRef(0);

  /* ---------- Filters/UI ---------- */
  const [activeSubject, setActiveSubject] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("");

  /* ---------- Form state ---------- */
  const [editingId, setEditingId] = useState(null);
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("Medium");
  const [marks, setMarks] = useState(1);
  const [negativeMarks, setNegativeMarks] = useState(0);
  const [questionType, setQuestionType] = useState("mcq");
  const [sectionKey, setSectionKey] = useState(""); // per subject

  /* ---------- Editors ---------- */
  const [questionHtml, setQuestionHtml] = useState("");
  const [explanationHtml, setExplanationHtml] = useState("");

  /* ---------- Type-specific ---------- */
  const [options, setOptions] = useState(emptyOptionSet());
  const [trueFalseAnswer, setTrueFalseAnswer] = useState("True");

  // NEW Match-the-Following state
  const [matchCount, setMatchCount] = useState(4);
  const [matchOptions, setMatchOptions] = useState([[], [], [], []]); // 4 arrays
  const [correctMappingIdx, setCorrectMappingIdx] = useState(0);

  const [assertionHtml, setAssertionHtml] = useState("");
  const [reasonHtml, setReasonHtml] = useState("");
  const [arAnswer, setArAnswer] = useState("A");
  const [statements, setStatements] = useState([emptyStatementRow(), emptyStatementRow(), emptyStatementRow()]);
  const [passageHtml, setPassageHtml] = useState("");
  const [caseSubQs, setCaseSubQs] = useState([emptyCaseSubQ()]);
  const [descPartHtml, setDescPartHtml] = useState("");

  /* ---------- Toast ---------- */
  const [toast, setToast] = useState({ show: false, title: "", body: "" });
  const [bulkType, setBulkType] = useState("auto"); // auto = read questionType column; otherwise force
const [dragOver, setDragOver] = useState(false);
const [selectedFile, setSelectedFile] = useState(null);
const fileInputRef = useRef(null);

  /* ---------- Jodit (Full + Mini) ---------- */
  const fullJodit = (readOnly) => ({
     readonly: !!readOnly,
  height: 220,
  toolbar: true,
  toolbarAdaptive: true,
  toolbarSticky: false,

  // paste behavior
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: "insert_clear_html",
  pasteHTMLAction: "insert",
  processPasteHTML: (html) => html,

  // images as base64
  uploader: { insertImageAsBase64URI: true },

  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  });

  const miniJodit = (readOnly) => ({
    readonly: !!readOnly,
  height: 140,
  toolbar: true,
  toolbarAdaptive: false,
  toolbarSticky: false,
  buttons: [
    "bold","italic","underline","|",
    "superscript","subscript","|",
    "align","|",                 // alignment dropdown (left/center/right/justify)
    "ul","ol","|",
    "link","image","symbol","|", // special characters = "symbol"
    "eraser"
  ].join(","),
  removeButtons: [
    "fullsize","source","print","preview","file","copyformat",
    "selectall","spellcheck","about"
  ],
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: "insert_clear_html",
  pasteHTMLAction: "insert",
  processPasteHTML: (html) => html,
  uploader: { insertImageAsBase64URI: true },
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  });

  const disabledBySchool = () => !(hookIsActive ?? (school?.active ?? (String(school?.status || "").toLowerCase() === "active")));
  const disabled = disabledBySchool();

  const joditFullConfig = useMemo(() => fullJodit(disabled), [disabled]);
  const joditMiniConfig = useMemo(() => miniJodit(disabled), [disabled]);

  const [showPreview, setShowPreview] = useState(false);

  /* ---------- Bulk preview / selection / exports ---------- */
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkPreview, setShowBulkPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewTitle, setPreviewTitle] = useState("Preview");
  const previewListRef = useRef([]); // keep the list used to build preview so actions can target the same set

  /* ---------- Status / Missing ---------- */
  const [showMissingModal, setShowMissingModal] = useState(false);

  /* ---------- Load template ---------- */
  useEffect(() => {
    (async () => {
      if (!schoolId || !templateId) return;
      setTLoading(true);
      try {
        const ref = doc(db, "schools", schoolId, "examTemplates", templateId);
        const snap = await getDoc(ref);
        const t = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        if (!t) {
          setToast({ show: true, title: "Template not found", body: templateId });
        }
        setTemplate(t);
      } catch (e) {
        setToast({ show: true, title: "Failed to load template", body: e?.message || "Unexpected error" });
      } finally {
        setTLoading(false);
      }
    })();
  }, [schoolId, templateId]);

  /* ---------- Subjects & Sections ---------- */
  const subjects = useMemo(() => {
    const arr = template?.subjectWise || [];
    return arr.map(s => ({
      name: s.subjectName,
      quota: (s.sections || []).reduce((a, sec) => a + (+sec.questionCount || 0), 0),
      sections: s.sections || []
    }));
  }, [template]);

  const sectionOptions = useMemo(() => {
    const subj = subjects.find(s => s.name === activeSubject);
    const secs = subj?.sections || [];
    return secs.map(s => ({
      key: s.key || "",
      label: s.label || `Section ${s.key}`,
      quota: +s.questionCount || 0,
      id: s.id
    }));
  }, [subjects, activeSubject]);

  useEffect(() => {
    if (subjects.length && !activeSubject) setActiveSubject(subjects[0].name);
  }, [subjects, activeSubject]);

  useEffect(() => {
    if (!sectionOptions.length) { setSectionKey(""); return; }
    if (!sectionOptions.some(s => s.key === sectionKey)) setSectionKey(sectionOptions[0].key);
  }, [sectionOptions]); // eslint-disable-line

  useEffect(() => {
    if (!template) return;
    const et = String(template.examType || "").toLowerCase();
    if (et === "descriptive") setQuestionType("descriptive");
    else if (et === "objective") setQuestionType("mcq");
  }, [template]);

  /* ---------- Live questions list with index fallback ---------- */
useEffect(() => {
  if (!schoolId || !templateId) return;

  hasLoadedRef.current = false;
  prevLenRef.current = 0;
  setSelectedIds(new Set());
  setUsingIndexFallback(false);

  setQLoading(true);
  const refCol = collection(db, "schools", schoolId, "questions");

  let unsub = onSnapshot(
    query(
      refCol,
      where("templateId", "==", templateId),
      orderBy("createdAt", "desc"),
      limit(800)
    ),
    (snap) => {
      const list = snap.docs.map((d) => {
        const data = d.data() || {};
        const neg =
          typeof data.negativeMarks === "number"
            ? data.negativeMarks
            : Number(data.negativeMarks) || 0;

        return { id: d.id, ...data, negativeMarks: neg };
      });

      setQuestions(list);
      setQLoading(false);

      if (!hasLoadedRef.current) {
        hasLoadedRef.current = true;
        prevLenRef.current = list.length;
        setToast({
          show: true,
          title: "Questions loaded",
          body: `Found ${list.length} question(s)`,
        });
        return;
      }

      if (list.length !== prevLenRef.current) {
        const diff = list.length - prevLenRef.current;
        prevLenRef.current = list.length;
        setToast({
          show: true,
          title: diff > 0 ? "Questions added" : "Questions removed",
          body:
            diff > 0
              ? `+${diff} (total ${list.length})`
              : `${Math.abs(diff)} deleted (total ${list.length})`,
        });
      }
    },
    (err) => {
      const msg = String(err?.message || "");
      if (msg.includes("The query requires an index")) {
        // fallback: no orderBy
        const unsub2 = onSnapshot(
          query(refCol, where("templateId", "==", templateId)),
          (snap2) => {
            const list = snap2.docs.map((d) => {
              const data = d.data() || {};
              const neg =
                typeof data.negativeMarks === "number"
                  ? data.negativeMarks
                  : Number(data.negativeMarks) || 0;

              return { id: d.id, ...data, negativeMarks: neg };
            });

            list.sort((a, b) => {
              const ta =
                a?.createdAt?.toMillis?.() ??
                a?.createdAt?.seconds * 1000 ??
                0;
              const tb =
                b?.createdAt?.toMillis?.() ??
                b?.createdAt?.seconds * 1000 ??
                0;
              return tb - ta;
            });

            setQuestions(list);
            setUsingIndexFallback(true);
            setQLoading(false);

            if (!hasLoadedRef.current) {
              hasLoadedRef.current = true;
              prevLenRef.current = list.length;
              setToast({
                show: true,
                title: "Questions loaded (fallback)",
                body: `Found ${list.length} (no index)`,
              });
            }
          },
          (err2) => {
            setQLoading(false);
            setToast({
              show: true,
              title: "Failed to load (fallback)",
              body: err2?.message || "Unexpected error",
            });
          }
        );
        setToast({
          show: true,
          title: "Index missing",
          body: "Using fallback (no server-side sort). Add composite index later.",
        });
        unsub = unsub2;
      } else {
        setQLoading(false);
        setToast({
          show: true,
          title: "Failed to load questions",
          body: err?.message || "Unexpected error",
        });
      }
    }
  );

  return () => unsub && unsub();
}, [schoolId, templateId]);


// Convert [[…],[…],[…],[…]] → { o1:[…], o2:[…], o3:[…], o4:[…] }
const toMatchOptionsObj = (arrs = [], n = 4) =>
  Object.fromEntries(arrs.slice(0, 4).map((a, i) => [`o${i + 1}`, (a || []).slice(0, n)]));

// Read either legacy array-of-arrays or the new object-of-arrays → [[…],[…],[…],[…]]
const readMatchOptions = (src, n = 4) => {
  if (Array.isArray(src)) return src.slice(0, 4).map(a => (a || []).slice(0, n));
  if (src && typeof src === "object") {
    return Object.keys(src)
      .sort() // o1, o2, ...
      .slice(0, 4)
      .map(k => (src[k] || []).slice(0, n));
  }
  return buildMatchOptions(n);
};


  /* ---------- Derived: progress & filtering ---------- */
  const perSubjectCounts = useMemo(() => {
    const map = {};
    subjects.forEach(s => {
      map[s.name] = { total: 0, sections: {} };
      s.sections.forEach(sec => { map[s.name].sections[sec.key] = 0; });
    });
    questions.forEach(q => {
      if (!map[q.subject]) return;
      map[q.subject].total += 1;
      const k = q.sectionKey || "";
      if (k && map[q.subject].sections[k] !== undefined) map[q.subject].sections[k] += 1;
    });
    return map;
  }, [subjects, questions]);

  const filteredQuestions = useMemo(() => {
    return questions.filter(q => {
      if (activeSubject && q.subject !== activeSubject) return false;
      if (filterSection && (q.sectionKey || "") !== filterSection) return false;
      if (filterDifficulty && q.difficulty !== filterDifficulty) return false;
      if (filterText) {
        const needle = filterText.toLowerCase();
        const plain = (q.questionHtml || q.passageHtml || "").replace(/<[^>]*>/g, "").toLowerCase();
        const hay = [q.topic || "", q.difficulty || "", (q.questionType || ""), (q.sectionKey || ""), plain]
          .join(" ").toLowerCase();
        return hay.includes(needle);
      }
      return true;
    });
  }, [questions, activeSubject, filterSection, filterDifficulty, filterText]);

  /* ---------- Completion & Missing ---------- */
  const completion = useMemo(() => {
    let complete = true;
    const missing = [];
    subjects.forEach(s => {
      (s.sections || []).forEach(sec => {
        const need = +sec.questionCount || 0;
        const have = (perSubjectCounts[s.name]?.sections?.[sec.key]) ?? 0;
        const short = Math.max(0, need - have);
        if (short > 0) {
          complete = false;
          missing.push({
            subject: s.name,
            sectionKey: sec.key || "",
            label: sec.label || `Section ${sec.key}`,
            need, have, short
          });
        }
      });
    });
    const totalNeed = subjects.reduce(
      (a, s) => a + (s.sections || []).reduce((b, sec) => b + (+sec.questionCount || 0), 0), 0
    );
    const totalHave = subjects.reduce((a, s) => a + (perSubjectCounts[s.name]?.total || 0), 0);
    return { complete, missing, totalNeed, totalHave };
  }, [subjects, perSubjectCounts]);

  /* ---------- Jodit init for Match options ---------- */
  useEffect(() => {
    setMatchOptions(buildMatchOptions(matchCount));
    setCorrectMappingIdx(0);
  }, [matchCount]);

  /* ---------- Helpers ---------- */
  const hardReset = () => {
    setEditingId(null);
    setTopic("");
    setDifficulty("Medium");
    setMarks(template?.marksPerCorrect ?? 1);
    setNegativeMarks(template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0);
    setQuestionHtml("");
    setExplanationHtml("");
    setOptions(emptyOptionSet());
    setTrueFalseAnswer("True");

    setMatchCount(4);
    setMatchOptions(buildMatchOptions(4));
    setCorrectMappingIdx(0);

    setAssertionHtml("");
    setReasonHtml("");
    setArAnswer("A");
    setStatements([emptyStatementRow(), emptyStatementRow(), emptyStatementRow()]);
    setPassageHtml("");
    setCaseSubQs([emptyCaseSubQ()]);
    setDescPartHtml("");

    const et = String(template?.examType || "").toLowerCase();
    setQuestionType(et === "descriptive" ? "descriptive" : "mcq");

    const firstSec = sectionOptions[0]?.key;
    setSectionKey(firstSec || "");
  };

  const startEdit = (q) => {
    setNegativeMarks(
    typeof q.negativeMarks === "number"
      ? q.negativeMarks
      : (Number(q.negativeMarks) || 0)
  );
    setEditingId(q.id);
    setTopic(q.topic || "");
    setDifficulty(q.difficulty || "Medium");
    setMarks(q.marks ?? (template?.marksPerCorrect ?? 1));
    setNegativeMarks(q.negativeMarks ?? (template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0));
    setQuestionType(q.questionType || "mcq");
    setQuestionHtml(q.questionHtml || "");
    setExplanationHtml(q.explanationHtml || "");
    setActiveSubject(q.subject || subjects[0]?.name || "");
    setSectionKey(q.sectionKey || sectionOptions[0]?.key || "");

    setOptions(q.options?.length ? q.options : emptyOptionSet());
    setTrueFalseAnswer(q.trueFalseAnswer || "True");

    // Match: support new and legacy
   if (q.questionType === "match") {
  // n from doc (pair count or infer), then read both legacy and new shapes
  const n = Math.max(
    2,
    Number(q.matchPairCount) ||
      (Array.isArray(q.matchOptions) && q.matchOptions[0]?.length) ||
      (q.matchOptionsObj && (Object.values(q.matchOptionsObj)[0] || []).length) ||
      4
  );
  setMatchCount(n);
  // Support both: legacy array-of-arrays or new object-of-arrays
  const arrs = readMatchOptions(q.matchOptions ?? q.matchOptionsObj, n);
  setMatchOptions(arrs);
  setCorrectMappingIdx(Number.isInteger(q.correctOptionIndex) ? q.correctOptionIndex : 0);
} else {
  setMatchCount(4);
  setMatchOptions(buildMatchOptions(4));
  setCorrectMappingIdx(0);
}


    setAssertionHtml(q.assertionHtml || "");
    setReasonHtml(q.reasonHtml || "");
    setArAnswer(q.arAnswer || "A");
    setStatements(q.statements?.length ? q.statements : [emptyStatementRow(), emptyStatementRow(), emptyStatementRow()]);
    setPassageHtml(q.passageHtml || "");
    setCaseSubQs(q.caseSubQs?.length ? q.caseSubQs : [emptyCaseSubQ()]);
    setDescPartHtml(q.descPartHtml || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---------- CRUD: Delete ---------- */
  const deleteQuestion = async (q) => {
    if (!schoolId) return;
    if (!window.confirm("Delete this question?")) return;
    try {
      await deleteDoc(doc(db, "schools", schoolId, "questions", q.id));
      setToast({
        show: true,
        title: "Question deleted",
        body: q.topic || `${q.subject}${q.sectionKey ? ` · ${q.sectionKey}` : ""}`
      });
    } catch (e) {
      setToast({ show: true, title: "Delete failed", body: e?.message || "Could not delete question" });
    }
  };

  const setCorrectSingle = (idx) => setOptions(opts => opts.map((o, i) => ({ ...o, isCorrect: i === idx })));
  const toggleCorrectMulti = (idx) => setOptions(opts => opts.map((o, i) => (i === idx ? { ...o, isCorrect: !o.isCorrect } : o)));

  const addStatement = () => setStatements((s) => [...s, emptyStatementRow()]);
  const removeStatement = (id) => setStatements((s) => s.filter(x => x.id !== id));
  const addCaseSubQ = () => setCaseSubQs((arr) => [...arr, emptyCaseSubQ()]);
  const removeCaseSubQ = (id) => setCaseSubQs((arr) => arr.length > 1 ? arr.filter(x => x.id !== id) : arr);

  /* ---------- Validation ---------- */
  const validateByType = () => {
    const hasStem = questionType === "case_based"
      ? (passageHtml || questionHtml)
      : (questionHtml || /<img/i.test(questionHtml));
    if (!hasStem && questionType !== "case_based") {
      alert("Question content cannot be empty.");
      return false;
    }

    if (questionType === "mcq") {
      if (options.filter(o => o.isCorrect).length !== 1) {
        alert("Single-correct MCQ must have exactly one correct option.");
        return false;
      }
    }
    if (questionType === "multi_correct") {
      if (!options.some(o => o.isCorrect)) {
        alert("Multi-correct MCQ needs at least one correct option.");
        return false;
      }
    }
    if (questionType === "true_false") {
      if (!["True","False"].includes(trueFalseAnswer)) {
        alert("Pick True or False.");
        return false;
      }
    }
    if (questionType === "match") {
      if (matchCount < 2) { alert("Select at least 2 pairs."); return false; }
      const ok = matchOptions.length === 4 &&
                 matchOptions.every(o => Array.isArray(o) && o.length === matchCount && o.every(v => v !== null && v !== undefined));
      if (!ok) { alert("Click Regenerate Options."); return false; }
      if (correctMappingIdx == null) { alert("Select the correct option."); return false; }
    }
    if (questionType === "assertion_reason") {
      if (!assertionHtml || !reasonHtml) {
        alert("Provide both Assertion and Reason.");
        return false;
      }
      if (!["A","B","C","D"].includes(arAnswer)) {
        alert("Pick a valid Assertion-Reason answer (A/B/C/D).");
        return false;
      }
    }
    if (questionType === "statement_based") {
      const any = statements.some(s => s.html);
      if (!any) {
        alert("Add at least one statement.");
        return false;
      }
    }
    if (questionType === "case_based") {
      if (!passageHtml) {
        alert("Provide the case/passage.");
        return false;
      }
      const validSubs = caseSubQs.filter(sq => sq.questionHtml && ["A","B","C","D"].includes(sq.correctKey));
      if (validSubs.length === 0) {
        alert("Add at least one valid sub-question.");
        return false;
      }
    }
    if (questionType === "mcq_descriptive") {
      if (options.filter(o => o.isCorrect).length !== 1) {
        alert("MCQ part must have exactly one correct option.");
        return false;
      }
      if (!descPartHtml) {
        alert("Provide the descriptive part.");
        return false;
      }
    }
    return true;
  };

  /* ---------- Save (Create/Update) ---------- */
  // Mirror a created question into the Question Bank
const pushToQuestionBank = async (payload, meta = {}) => {
  if (!schoolId) return;
  const bankPayload = {
    ...payload,
    templateId,
    source: meta.source || "template",  // "manual" for single add, "bulk" for bulk, etc.
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await addDoc(collection(db, "schools", schoolId, "questionbank"), bankPayload);
};

 const saveQuestion = async () => {
  if (disabled) return alert("School inactive.");
  if (!schoolId || !templateId || !activeSubject) return;
  if (!validateByType()) return;

  const tType = String(template?.examType || "").toLowerCase();
  let finalType = questionType;
  if (tType === "objective" && ["descriptive"].includes(finalType)) finalType = "mcq";
  if (tType === "descriptive") finalType = "descriptive";

  let typeData = {};
  switch (finalType) {
    case "mcq": typeData = { options, singleCorrect: true }; break;
    case "multi_correct": typeData = { options, singleCorrect: false }; break;
    case "true_false": typeData = { trueFalseAnswer }; break;
    case "descriptive": typeData = {}; break;
    case "match":
      typeData = {
        matchPairCount: matchCount,
        labelScheme: { left: "alpha", right: "roman" },
        matchOptions: matchOptions.map(o => o.slice(0, matchCount)),
        correctOptionIndex: correctMappingIdx,
      };
      break;
    case "assertion_reason": typeData = { assertionHtml, reasonHtml, arAnswer }; break;
    case "statement_based": typeData = { statements }; break;
    case "case_based":
      typeData = {
        passageHtml,
        caseSubQs: caseSubQs
          .map(sq => ({ id: sq.id, questionHtml: sq.questionHtml, options: sq.options, correctKey: sq.correctKey }))
          .filter(sq => sq.questionHtml),
      };
      break;
    case "mcq_descriptive":
      typeData = { options, singleCorrect: true, descPartHtml };
      break;
    default: break;
  }

  const payload = {
    templateId,
    subject: activeSubject,
    sectionKey: sectionKey || "",
    topic: topic.trim(),
    difficulty,
    questionType: finalType,
    questionHtml,
    explanationHtml,
    marks: Number(marks) || (template?.marksPerCorrect ?? 1),
    negativeMarks: Number(negativeMarks) || (template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0),
    ...typeData,
    updatedAt: serverTimestamp(),
  };

  try {
    const clean = sanitizeQuestionForWrite(payload);

    if (editingId) {
      await setDoc(
        doc(db, "schools", schoolId, "questions", editingId),
        clean,
        { merge: true }
      );
      setToast({
        show: true,
        title: "Question updated",
        body: `${activeSubject}${sectionKey ? ` · Section ${sectionKey}` : ""}`
      });
    } else {
      // Create in questions
      await addDoc(
        collection(db, "schools", schoolId, "questions"),
        { ...clean, createdAt: serverTimestamp() }
      );
      // Mirror in questionbank
      await pushToQuestionBank(clean, { source: "manual" });

      setToast({
        show: true,
        title: "Question added",
        body: `${activeSubject}${sectionKey ? ` · Section ${sectionKey}` : ""}`
      });
    }
    hardReset();
    setShowPreview(false);
  } catch (e) {
    setToast({ show: true, title: "Save failed", body: e?.message || "Could not save question" });
  }
};

const onAnyFile = (file) => {
  if (!file) return;
  setSelectedFile(file);

  const ext = file.name.toLowerCase().split(".").pop();
 if (ext === "xlsx" || ext === "xls") {
    onExcelFile(file);                             // you already have this
  } else {
    setBulkErrors((errs) => [...errs, `Unsupported file type: ${ext}`]);
  }
};
  /* ---------- Bulk CSV/Excel ---------- */
  const [bulkErrors, setBulkErrors] = useState([]);

  const parseBool = (v) => {
    const s = String(v || "").trim().toLowerCase();
    return ["true","t","1","yes","y"].includes(s);
  };

  const validateSectionForSubject = (subjectName, secKey) => {
    if (!secKey) return "";
    const subj = subjects.find(s => s.name === subjectName);
    const keys = new Set((subj?.sections || []).map(x => x.key));
    return keys.has(String(secKey)) ? String(secKey) : "";
  };

  async function saveRow(r, forcedType = "auto") {
  // resolve type
  const qtRaw = (r.questionType && String(r.questionType).trim()) || "";
  const resolved = normalizeType(qtRaw || (forcedType !== "auto" ? forcedType : "mcq"));
  const qt = resolved;

  // small validators by type (best-effort; still robust to partial data)
  const requireAny = (val) => !!(val && String(val).trim() !== "");
  const requireAtLeastOne = (...vals) => vals.some(requireAny);

  // Build base
  const base = {
    templateId,
    subject: r.subject,
    sectionKey: validateSectionForSubject(r.subject, r.sectionKey),
    topic: r.topic || "",
    difficulty: DIFFICULTY.includes(r.difficulty) ? r.difficulty : "Medium",
    questionType: qt,
    marks: Number(r.marks) || (template?.marksPerCorrect ?? 1),
    negativeMarks: Number(r.negativeMarks) || (template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0),
    explanationHtml: r.explanationHtml || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  let payload = {};
  if (qt === "mcq" || qt === "multi_correct" || qt === "mcq_descriptive") {
    // require: stem, at least 2 options, at least 1 correct (for mcq_descriptive + mcq) / allow multi for multi_correct
    const options = ["optionA","optionB","optionC","optionD"].map((k, idx) => ({
      key: ["A","B","C","D"][idx],
      value: r[k] || "",
      isCorrect: false,
    })).filter(o => requireAny(o.value));

    if (r.correct) {
      const set = new Set(String(r.correct).toUpperCase().split(",").map(x => x.trim()));
      options.forEach(o => o.isCorrect = set.has(o.key));
    }

    payload = {
      ...base,
      questionHtml: r.questionHtml || "",
      options,
      singleCorrect: qt === "multi_correct" ? false : true,
      ...(qt === "mcq_descriptive" ? { descPartHtml: r.descPartHtml || "" } : {}),
    };

    // quick validations
    if (!requireAny(payload.questionHtml)) throw new Error("Missing questionHtml");
    if (options.length < 2) throw new Error("Need >= 2 options");
    const correctCount = options.filter(o => o.isCorrect).length;
    if (qt !== "multi_correct" && correctCount !== 1) throw new Error("Need exactly 1 correct");
    if (qt === "multi_correct" && correctCount < 1) throw new Error("Need >= 1 correct");

  } else if (qt === "descriptive") {
    if (!requireAny(r.questionHtml)) throw new Error("Missing questionHtml");
    payload = { ...base, questionHtml: r.questionHtml || "" };

  } else if (qt === "true_false") {
    if (!requireAny(r.questionHtml)) throw new Error("Missing questionHtml");
    const ans = String(r.trueFalseAnswer || "").trim();
    payload = {
      ...base,
      questionHtml: r.questionHtml || "",
      trueFalseAnswer: (ans === "False" ? "False" : "True"),
    };

} else if (qt === "match") {
  if (!requireAny(r.questionHtml)) throw new Error("Missing questionHtml");
  let count = 0;
  for (let i = 1; i <= 26; i++) {
    const L = r[`left${i}`], R = r[`right${i}`];
    if (!L && !R) break;
    count++;
  }
  const n = Math.max(2, count || 4);
  const opts = buildMatchOptions(n);
  
  payload = {
    ...base,
    questionHtml: r.questionHtml || "",
    matchPairCount: n,
    labelScheme: { left: "alpha", right: "roman" },
    // store as object to avoid nested arrays:
    matchOptionsObj: toMatchOptionsObj(opts, n),
    correctOptionIndex: 0,
  };

  } else if (qt === "assertion_reason") {
    if (!requireAny(r.assertionHtml) || !requireAny(r.reasonHtml)) {
      throw new Error("Need assertionHtml and reasonHtml");
    }
    payload = {
      ...base,
      assertionHtml: r.assertionHtml || "",
      reasonHtml: r.reasonHtml || "",
      arAnswer: ["A","B","C","D"].includes(String(r.arAnswer).toUpperCase()) ? String(r.arAnswer).toUpperCase() : "A",
      questionHtml: r.questionHtml || "",
    };

  } else if (qt === "statement_based") {
    const stmts = [];
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    for (let i = 0; i < letters.length; i++) {
      const L = letters[i];
      const html = r[`s${L}_html`] ?? r[`s${i+1}_html`];
      const truth = r[`s${L}_true`] ?? r[`s${i+1}_true`];
      if (!html && truth === undefined) { if (i > 0) break; }
      if (html) {
        stmts.push({ id: crypto.randomUUID(), key: L, html, isTrue: parseBool(truth) });
      }
    }
    if (stmts.length === 0) throw new Error("Need at least one statement (sA_html etc.)");
    payload = { ...base, questionHtml: r.questionHtml || "", statements: stmts };

  } else if (qt === "case_based") {
  if (!requireAny(r.passageHtml)) throw new Error("Missing passageHtml");
  const subs = [];
  for (let i = 1; i <= 20; i++) {
    const qh = r[`sub${i}_questionHtml`];
    const A = r[`sub${i}_optionA`], B = r[`sub${i}_optionB`],
          C = r[`sub${i}_optionC`], D = r[`sub${i}_optionD`];
    const ck = String(r[`sub${i}_correct`] || "A").toUpperCase();
    if (!requireAtLeastOne(qh, A, B, C, D)) break;
    subs.push({
      // no 'id' here
      questionHtml: qh || "",
      options: [
        { key: "A", value: A || "", isCorrect: ck === "A" },
        { key: "B", value: B || "", isCorrect: ck === "B" },
        { key: "C", value: C || "", isCorrect: ck === "C" },
        { key: "D", value: D || "", isCorrect: ck === "D" },
      ],
      correctKey: ["A","B","C","D"].includes(ck) ? ck : "A",
    });
  }
  if (subs.length === 0) throw new Error("Need at least one sub question (sub1_*)");

  payload = { ...base, passageHtml: r.passageHtml || "", caseSubQs: subs };
}

  if (!("questionHtml" in payload)) payload.questionHtml = r.questionHtml || "";

  // write to questions
  await addDoc(collection(db, "schools", schoolId, "questions"), payload);
  // mirror to bank
  await pushToQuestionBank(payload, { source: "bulk" });
}


const parseCsv = async (file) => {
  setBulkErrors([]);
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: async (res) => {
      const rows = res.data || [];
      const errors = [];
      let saved = 0;
      const allowedSubjects = new Set(subjects.map(s => s.name));
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (!r.subject || !allowedSubjects.has(r.subject)) {
          errors.push(`Row ${i+1}: missing/invalid subject`);
          continue;
        }
        try {
          await saveRow(r, bulkType);
          saved++;
        } catch (e) {
          errors.push(`Row ${i+1}: ${e?.message || "save failed"}`);
        }
      }
      if (errors.length) setBulkErrors(errors);
      if (saved) setToast({ show: true, title: "Bulk upload", body: `Saved ${saved} question(s)` });
    },
    error: (err) => {
      setBulkErrors([err.message]);
      setToast({ show: true, title: "Upload failed", body: err.message });
    },
  });
};

const onExcelFile = async (file) => {
  setBulkErrors([]);
  try {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
    const errors = [];
    let saved = 0;
    const allowedSubjects = new Set(subjects.map(s => s.name));
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.subject || !allowedSubjects.has(r.subject)) {
        errors.push(`Row ${i+1}: missing/invalid subject`);
        continue;
      }
      try {
        await saveRow(r, bulkType);
        saved++;
      } catch (e) {
        errors.push(`Row ${i+1}: ${e?.message || "save failed"}`);
      }
    }
    if (errors.length) setBulkErrors(errors);
    if (saved) setToast({ show: true, title: "Bulk upload", body: `Saved ${saved} question(s)` });
  } catch (e) {
    setBulkErrors([e?.message || "Failed to read Excel file"]);
    setToast({ show: true, title: "Upload failed", body: e?.message || "Failed to read Excel file" });
  }
};

// Build CSV header + one example row for a given bulk type
const buildCsvTemplateForType = (type) => {
  const firstSubj = subjects[0];

  // columns common to all types
  const COMMON = [
    "subject","sectionKey","topic","difficulty","marks","negativeMarks","explanationHtml"
  ];

  switch (type) {
    case "mcq": {
      const header = [
        ...COMMON,
        "questionHtml","optionA","optionB","optionC","optionD","correct",
      ];
      const example = {
        subject: firstSubj?.name || "Physics",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Kinematics",
        difficulty: "Medium",
        marks: template?.marksPerCorrect ?? 1,
        negativeMarks: template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0,
        explanationHtml: "Why A is right…",
        questionHtml: "Displacement is the area under the ___ graph.",
        optionA: "Velocity–Time", optionB: "Acceleration–Time", optionC: "Jerk–Time", optionD: "Position–Time",
        correct: "A",
      };
      return { header, example };
    }

    case "multi_correct": {
      const header = [
        ...COMMON,
        "questionHtml","optionA","optionB","optionC","optionD","correct" // e.g. "A,B"
      ];
      const example = {
        subject: firstSubj?.name || "Chemistry",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Periodic Table",
        difficulty: "Medium",
        marks: template?.marksPerCorrect ?? 1,
        negativeMarks: template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0,
        explanationHtml: "A and C are both correct…",
        questionHtml: "Select all alkali metals:",
        optionA: "Li", optionB: "Mg", optionC: "Na", optionD: "Al",
        correct: "A,C",
      };
      return { header, example };
    }

    case "mcq_descriptive": {
      const header = [
        ...COMMON,
        "questionHtml","optionA","optionB","optionC","optionD","correct","descPartHtml"
      ];
      const example = {
        subject: firstSubj?.name || "Biology",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Cell",
        difficulty: "Medium",
        marks: template?.marksPerCorrect ?? 1,
        negativeMarks: template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0,
        explanationHtml: "Rationale…",
        questionHtml: "Which organelle is the powerhouse of the cell?",
        optionA: "Mitochondria", optionB: "Ribosome", optionC: "Golgi", optionD: "Lysosome",
        correct: "A",
        descPartHtml: "Explain how ATP is produced.",
      };
      return { header, example };
    }

    case "true_false": {
      const header = [
        ...COMMON,
        "questionHtml","trueFalseAnswer"
      ];
      const example = {
        subject: firstSubj?.name || "Physics",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Vectors",
        difficulty: "Easy",
        marks: template?.marksPerCorrect ?? 1,
        negativeMarks: template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0,
        explanationHtml: "Explanation",
        questionHtml: "Vector addition is commutative.",
        trueFalseAnswer: "True",
      };
      return { header, example };
    }

    case "descriptive": {
      const header = [
        ...COMMON,
        "questionHtml"
      ];
      const example = {
        subject: firstSubj?.name || "English",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Essay",
        difficulty: "Medium",
        marks: 5,
        negativeMarks: 0,
        explanationHtml: "Explanation",
        questionHtml: "Write a short note on climate resilience (150–200 words).",
      };
      return { header, example };
    }
let typeData = {};
   case "match": {
  const n = matchCount;
  typeData = {
    matchPairCount: n,
    labelScheme: { left: "alpha", right: "roman" },
    // ❌ matchOptions: [[],[],[],[]]  // causes nested arrays error
    // ✅ store as object of arrays:
    matchOptionsObj: toMatchOptionsObj(matchOptions, n),
    correctOptionIndex: correctMappingIdx,
  };
  break;
}

    case "assertion_reason": {
      const header = [
        ...COMMON,
        "assertionHtml","reasonHtml","arAnswer" // A/B/C/D
      ];
      const example = {
        subject: firstSubj?.name || "Physics",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Thermodynamics",
        difficulty: "Hard",
        marks: template?.marksPerCorrect ?? 1,
        negativeMarks: template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0,
        explanationHtml: "<p>…</p>",
        assertionHtml: "A: Heat is a form of energy.>",
        reasonHtml: "R: Energy can neither be created nor destroyed.",
        arAnswer: "A",
      };
      return { header, example };
    }

    case "statement_based": {
      const header = [
        ...COMMON,
        "questionHtml",
        "sA_html","sA_true","sB_html","sB_true","sC_html","sC_true","sD_html","sD_true"
      ];
      const example = {
        subject: firstSubj?.name || "Geography",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Climate",
        difficulty: "Medium",
        marks: template?.marksPerCorrect ?? 1,
        negativeMarks: template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0,
        explanationHtml: "…",
        questionHtml: "Identify the true statements:",
        sA_html: "Equator has high rainfall.",  sA_true: "true",
        sB_html: "Poles receive most sunlight.", sB_true: "false",
      };
      return { header, example };
    }

    case "case_based":
    default: {
      const header = [
        ...COMMON,
        "passageHtml",
        "sub1_questionHtml","sub1_optionA","sub1_optionB","sub1_optionC","sub1_optionD","sub1_correct",
        "sub2_questionHtml","sub2_optionA","sub2_optionB","sub2_optionC","sub2_optionD","sub2_correct"
      ];
      const example = {
        subject: firstSubj?.name || "Physics",
        sectionKey: firstSubj?.sections?.[0]?.key || "",
        topic: "Kinematics – Graphs",
        difficulty: "Medium",
        marks: template?.marksPerCorrect ?? 1,
        negativeMarks: template?.allowNegativeMarking ? (template?.marksPerWrong ?? 0) : 0,
        explanationHtml: "Explanation",
        passageHtml: "Read the passage and answer Q1–Q2",
        sub1_questionHtml: "What does the slope represent?",
        sub1_optionA: "Velocity", sub1_optionB: "Acceleration", sub1_optionC: "Jerk", sub1_optionD: "Displacement", sub1_correct: "A",
        sub2_questionHtml: "Area under the curve equals…",
        sub2_optionA: "Velocity", sub2_optionB: "Displacement", sub2_optionC: "Power", sub2_optionD: "Force", sub2_correct: "B",
      };
      return { header, example };
    }
  }
};

 const downloadCsvTemplate = () => {
  // use currently selected bulkType to shape the template
  const { header, example } = buildCsvTemplateForType(bulkType || "mcq");

  // include the type column so users can see it (optional)
  const finalHeader = ["questionType", ...header];
  const finalExample = { questionType: bulkType || "mcq", ...example };

  const csv = Papa.unparse({
    fields: finalHeader,
    data: [finalHeader.map(h => finalExample[h] ?? "")]
  });

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const fname = (template?.name || templateId || "questions-template").replace(/[^\w\\-]+/g, "_");
  a.href = url;
  a.download = `questions-template-${fname}-${(bulkType||"mcq")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};


  /* ---------- Selection + bulk actions + preview/export ---------- */
  const selectedQuestions = useMemo(
    () => questions.filter(q => selectedIds.has(q.id)),
    [questions, selectedIds]
  );
  const clearSelection = () => setSelectedIds(new Set());
  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllFiltered = () => {
    const allIds = filteredQuestions.map(q => q.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
    setSelectedIds(() => allSelected ? new Set() : new Set(allIds));
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected question(s)?`)) return;
    let ok = 0, fail = 0;
    for (const id of selectedIds) {
      try { await deleteDoc(doc(db, "schools", schoolId, "questions", id)); ok++; }
      catch (e) { fail++; }
    }
    clearSelection();
    if (ok) setToast({ show: true, title: "Bulk delete", body: `Deleted ${ok}${fail ? `, ${fail} failed` : ""}` });
    if (fail && !ok) setToast({ show: true, title: "Bulk delete failed", body: "Could not delete selected items" });
  };

  const buildPaperHtml = (list, title = "Question Paper") => {
    // Subject & section order come from the template once
    const subjectOrderFromTemplate = (template?.subjectWise || []).map(s => s.subjectName);
    const sectionOrderMap = {};
    (template?.subjectWise || []).forEach(s => {
      sectionOrderMap[s.subjectName] = (s.sections || []).map(sec => sec.key || "");
    });

    const css = `
      <style>
        @page { margin: 20mm; }
        body { font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Inter,Helvetica,Arial,sans-serif; color: #111; }
        h1 { font-size: 20px; text-align:center; margin: 0 0 16px; }
        h2 { font-size: 16px; margin: 18px 0 8px; }
        h3 { font-size: 14px; margin: 10px 0 6px; }
        .meta { font-size: 12px; color: #555; margin-bottom: 6px; }
        .q { margin: 10px 0 14px; page-break-inside: avoid; }
        .marks { float: right; font-size: 12px; color: #444; }
        .clearfix::after { content: ""; display: table; clear: both; }
        ol.options { margin: 6px 0 0 18px; }
        .case { border: 1px dashed #999; padding: 10px; background: #fafafa; margin: 6px 0; }
        .pagebreak { page-break-after: always; }
        table.mtf { width: 100%; border-collapse: collapse; margin-top: 6px; }
        table.mtf td { border: 1px solid #ccc; padding: 6px; vertical-align: top; }
        .header { text-align:center; margin-bottom: 10px; }
        .subtle { color: #666; font-size: 12px; }
      </style>
    `;

    const renderQuestion = (q, i) => {
      const num = i + 1;
      const marksStr = q.marks ? `<span class="marks">[${q.marks}${q.negativeMarks ? ` / ${q.negativeMarks}` : ""}]</span>` : "";
      const stem = q.questionHtml || "";

      if (q.questionType === "case_based") {
        const subs = (q.caseSubQs || []).map((sq, sidx) => `
          <div><strong>(${sidx + 1})</strong> ${sq.questionHtml || ""}</div>
          <ol class="options" type="A">
            ${(sq.options || []).map(o => `<li>${o.value || ""}</li>`).join("")}
          </ol>
        `).join("");
        return `
          <div class="q clearfix">
            <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · CASE</div>
            <div><strong>Q${num}.</strong> Read the passage:</div>
            <div class="case">${q.passageHtml || ""}</div>
            ${subs}
            ${marksStr}
          </div>
        `;
      }

      if (q.questionType === "match") {
        // Legacy table:
        if ((q.matchPairs || []).some(r => r.leftHtml || r.rightHtml)) {
          const rows = (q.matchPairs || [])
            .filter(r => r.leftHtml || r.rightHtml)
            .map(r => `<tr><td>${r.leftHtml || ""}</td><td>${r.rightHtml || ""}</td></tr>`).join("");
          return `
            <div class="q clearfix">
              <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · MATCH</div>
              <div><strong>Q${num}.</strong> ${stem}</div>
              <table class="mtf"><tbody>${rows}</tbody></table>
              ${marksStr}
            </div>
          `;
        }
// New options rendering:
const n = Number(q.matchPairCount || 0);
// Read both shapes
const raw = q.matchOptions ?? q.matchOptionsObj;
const arrs = readMatchOptions(raw, n);
const opts = arrs.slice(0, 4).map((arr, idx) => {
  const txt = (arr && arr.length)
    ? arr.map((rIdx, lIdx) => `(${leftLabel(lIdx)})-(${rightLabel(rIdx)})`).join(", ")
    : "";
  const mark = (idx === q.correctOptionIndex) ? " <strong>(Correct)</strong>" : "";
  return `<div>Option ${idx + 1}: ${txt}${mark}</div>`;
}).join("");

        return `
          <div class="q clearfix">
            <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · MATCH</div>
            <div><strong>Q${num}.</strong> ${stem}</div>
            <div class="subtle">Pairs: ${n} (labels: a… / i…)</div>
            <div style="margin-top:6px">${opts}</div>
            ${marksStr}
          </div>
        `;
      }

      if (q.questionType === "assertion_reason") {
        return `
          <div class="q clearfix">
            <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · A/R</div>
            <div><strong>Q${num}.</strong></div>
            <div><em>Assertion:</em> ${q.assertionHtml || ""}</div>
            <div><em>Reason:</em> ${q.reasonHtml || ""}</div>
            <ol class="options">
              <li>A: Both statements true, Reason is the correct explanation</li>
              <li>B: Both statements true, but Reason is not the explanation</li>
              <li>C: Assertion true, Reason false</li>
              <li>D: Assertion false</li>
            </ol>
            ${marksStr}
          </div>
        `;
      }

      if (q.questionType === "statement_based") {
        const stmts = (q.statements || []).map(s => `<div>${(s.key || "")}. ${s.html || ""}</div>`).join("");
        return `
          <div class="q clearfix">
            <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · STATEMENT</div>
            <div><strong>Q${num}.</strong> ${stem}</div>
            <div>${stmts}</div>
            ${marksStr}
          </div>
        `;
      }

      if (q.questionType === "true_false") {
        return `
          <div class="q clearfix">
            <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · TRUE/FALSE</div>
            <div><strong>Q${num}.</strong> ${stem}</div>
            ${marksStr}
          </div>
        `;
      }

      if (q.questionType === "descriptive") {
        return `
          <div class="q clearfix">
            <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · DESCRIPTIVE</div>
            <div><strong>Q${num}.</strong> ${stem}</div>
            ${marksStr}
          </div>
        `;
      }

      const opts = (q.options || []).map(o => `<li>${o.value || ""}</li>`).join("");
      return `
        <div class="q clearfix">
          <div class="meta subtle">${q.subject}${q.sectionKey ? ` · Sec ${q.sectionKey}` : ""} · ${q.difficulty} · ${(q.questionType || "MCQ").toUpperCase()}</div>
          <div><strong>Q${num}.</strong> ${stem}</div>
          <ol class="options" type="A">${opts}</ol>
          ${q.questionType === "mcq_descriptive" ? `<div style="margin-top:8px;"><em>Descriptive:</em> ${q.descPartHtml || ""}</div>` : ""}
          ${marksStr}
        </div>
      `;
    };

    let htmlSections = "";

    // Subject order: template subjects first (only those present), then any others
    const subjectsInTemplateAndList = subjectOrderFromTemplate.filter(name =>
      list.some(q => q.subject === name)
    );
    const otherSubjects = [...new Set(list.map(q => q.subject))]
      .filter(n => !subjectsInTemplateAndList.includes(n))
      .sort();
    const finalSubjects = [...subjectsInTemplateAndList, ...otherSubjects];

    finalSubjects.forEach((subj, si) => {
      const group = list.filter(q => q.subject === subj);
      if (!group.length) return;

      const secOrder = sectionOrderMap[subj] || [];
      const secKeysInGroup = [...new Set(group.map(q => q.sectionKey || ""))];
      const orderedSecKeys = [
        ...secKeysInGroup.filter(k => secOrder.includes(k)).sort((a,b) => secOrder.indexOf(a) - secOrder.indexOf(b)),
        ...secKeysInGroup.filter(k => !secOrder.includes(k)).sort()
      ];

      htmlSections += `<h2>${subj}</h2>`;
      orderedSecKeys.forEach(secKey => {
        const secTitle = secKey ? `Section ${secKey}` : "General";
        const listQ = group
          .filter(q => (q.sectionKey || "") === secKey)
          .sort((a,b) => (a?.createdAt?.toMillis?.() ?? a?.createdAt?.seconds*1000 ?? 0) - (b?.createdAt?.toMillis?.() ?? b?.createdAt?.seconds*1000 ?? 0));
        htmlSections += `<h3>${secTitle}</h3>`;
        listQ.forEach((q, idx) => htmlSections += renderQuestion(q, idx));
      });

      if (si < finalSubjects.length - 1) htmlSections += `<div class="pagebreak"></div>`;
    });

    const hdr = `
      <div class="header">
        <h1>${template?.name || title}</h1>
        <div class="subtle">${template?.examType || ""}${usingIndexFallback ? " · (client-sorted)" : ""}</div>
      </div>
    `;

    return `<!doctype html><html><head><meta charset="utf-8" />${css}</head><body>${hdr}${htmlSections}</body></html>`;
  };

  const buildBulkPreviewHtml = (list) => buildPaperHtml(list, "Preview");

  const openPreviewSelected = () => {
    if (selectedIds.size === 0) return;
    const list = selectedQuestions;
    previewListRef.current = list;
    setPreviewTitle("Preview — Selected");
    setPreviewHtml(buildBulkPreviewHtml(list));
    setShowBulkPreview(true);
  };

  const openPreviewFiltered = () => {
    if (filteredQuestions.length === 0) return;
    const list = filteredQuestions;
    previewListRef.current = list;
    setPreviewTitle("Preview — Filtered");
    setPreviewHtml(buildBulkPreviewHtml(list));
    setShowBulkPreview(true);
  };

  const openPrintView = (list) => {
    if (!list?.length) return;
    const html = buildPaperHtml(list, "Question Paper");
    const w = window.open("", "_blank");
    if (!w) {
      setToast({ show: true, title: "Popup blocked", body: "Allow popups to open print view." });
      return;
    }
    w.document.open();
    w.document.write(html + `<script>window.addEventListener('load',()=>setTimeout(()=>window.print(),200));</script>`);
    w.document.close();
  };

  const downloadAsWord = (list) => {
    if (!list?.length) return;
    const html = buildPaperHtml(list, "Question Paper");
    const blob = new Blob([html], { type: "application/msword;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const filenameSafe = (template?.name || `question-paper-${templateId}`).replace(/[^\w\-]+/g, "_");
    a.href = url;
    a.download = `${filenameSafe}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  };
// state to show per-row saving
const [savingSectionIds, setSavingSectionIds] = useState(new Set());

// sections for each subject -> [{key,label}]
const sectionOptionsBySubject = useMemo(() => {
  const map = {};
  subjects.forEach(s => {
    map[s.name] = (s.sections || []).map(sec => ({
      key: sec.key || "",
      label: sec.label || (sec.key ? `Section ${sec.key}` : "General"),
    }));
  });
  return map;
}, [subjects]);

// inline update for a row
const updateQuestionSection = async (q, newKey) => {
  if (!schoolId || !q?.id) return;
  const validKeys = new Set((sectionOptionsBySubject[q.subject] || []).map(o => o.key));
  const value = validKeys.has(newKey) ? newKey : ""; // fallback to General

  setSavingSectionIds(prev => new Set(prev).add(q.id));
  try {
    await setDoc(
      doc(db, "schools", schoolId, "questions", q.id),
      { sectionKey: value, updatedAt: serverTimestamp() },
      { merge: true }
    );
    setToast({ show: true, title: "Section updated", body: `${q.subject} · ${value || "General"}` });
  } catch (e) {
    setToast({ show: true, title: "Update failed", body: e?.message || "Could not update section" });
  } finally {
    setSavingSectionIds(prev => { const n = new Set(prev); n.delete(q.id); return n; });
  }
};
  /* ---------- Template Status actions ---------- */
  const currentStatus = (template?.status === "ready" ? "ready" : "draft");
  const canMarkReady = completion.complete && currentStatus !== "ready" && !disabled;
  const canRevertDraft = currentStatus === "ready" && !disabled;

  const updateTemplateStatus = async (status) => {
    if (!schoolId || !templateId) return;
    try {
      await setDoc(
        doc(db, "schools", schoolId, "examTemplates", templateId),
        { status, statusUpdatedAt: serverTimestamp() },
        { merge: true }
      );
      setTemplate(t => t ? { ...t, status } : t);
      setToast({
        show: true,
        title: "Template status updated",
        body: `Now ${status.toUpperCase()}`
      });
    } catch (e) {
      setToast({ show: true, title: "Status update failed", body: e?.message || "Could not update status" });
    }
  };

  /* ---------- UI ---------- */
  if (tLoading || schoolLoading) return <div className="p-5 text-center"><Spinner /></div>;
  if (!template) {
    return (
      <div className="p-4">
        <Alert variant="danger">Template not found.</Alert>
        <Button variant="secondary" onClick={() => nav("/school/exams")}>Back</Button>
      </div>
    );
  }

  const et = String(template.examType || "").toLowerCase();
  const isMixed = et === "mixed";

  return (
    <div className="container-fluid p-0">
      {/* Toasts */}
<ToastContainer
  position="top-end"   // top-right corner
  className="p-3"
>
  <Toast
    bg="light"
    onClose={() => setToast(s => ({ ...s, show: false }))}
    show={toast.show}
    autohide={false}   // disable autohide → stays sticky
  >
    <Toast.Header>
      <strong className="me-auto">{toast.title || "Saved"}</strong>
      <small>just now</small>
    </Toast.Header>
    <Toast.Body>{toast.body}</Toast.Body>
  </Toast>
</ToastContainer>

      <Card className="border-0 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column">
            <div className="fw-semibold">Template Questions</div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <small className="text-muted">
                Template: <Badge bg="light" text="dark">{template.name}</Badge> · Type: {template.examType}
              </small>
              {usingIndexFallback && <Badge bg="warning" text="dark">Index fallback</Badge>}
              <Badge bg={currentStatus === "ready" ? "success" : "secondary"}>
                {currentStatus === "ready" ? "READY" : "DRAFT"}
              </Badge>
              <Badge bg={completion.complete ? "success" : "warning"} text={completion.complete ? undefined : "dark"}>
                {completion.totalHave}/{completion.totalNeed} filled
              </Badge>
              {!completion.complete && (
                <Button
                  size="sm"
                  variant="outline-warning"
                  onClick={() => setShowMissingModal(true)}
                >
                  View Missing
                </Button>
              )}
            </div>
          </div>

          <div className="d-flex gap-2">
            {/* Status actions */}
            <Dropdown as={ButtonGroup}>
              <Button
                variant={canMarkReady ? "success" : "outline-success"}
                disabled={!canMarkReady}
                onClick={() => updateTemplateStatus("ready")}
                title={completion.complete ? "Mark as Ready" : "Fill all sections to enable"}
              >
                Mark Ready
              </Button>
              <Dropdown.Toggle split variant="outline-success" />
              <Dropdown.Menu align="end">
                <Dropdown.Item
                  disabled={!canRevertDraft}
                  onClick={() => updateTemplateStatus("draft")}
                >
                  Revert to Draft
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Export / Print */}
            <Dropdown as={ButtonGroup}>
              <Button
                variant="outline-success"
                onClick={() => openPrintView(filteredQuestions)}
                disabled={!filteredQuestions.length}
                title="Open print dialog (you can save as PDF)"
              >
                Print / PDF (Filtered)
              </Button>
              <Dropdown.Toggle split variant="outline-success" />
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => openPrintView(selectedQuestions)} disabled={!selectedIds.size}>
                  Print / PDF (Selected)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => downloadAsWord(filteredQuestions)} disabled={!filteredQuestions.length}>
                  Download Word (.doc) — Filtered
                </Dropdown.Item>
                <Dropdown.Item onClick={() => downloadAsWord(selectedQuestions)} disabled={!selectedIds.size}>
                  Download Word (.doc) — Selected
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Proceed button (generic) */}
            <Button
              variant={currentStatus === "ready" ? "primary" : "outline-primary"}
              onClick={() => nav("/school/exams")}
              title={currentStatus === "ready" ? "Proceed to map/conduct exam" : "Complete & mark ready first"}
            >
              {currentStatus === "ready" ? "Proceed to Exam Setup" : "Go to Exams"}
            </Button>

            <Button variant="outline-secondary" onClick={() => nav("/school/exams")}>Back</Button>
          </div>
        </Card.Header>

        <Card.Body>
          {disabled && (
            <Alert variant="warning">School is inactive — adding/editing is disabled.</Alert>
          )}

          {/* Progress cards per subject */}
          <Row className="g-2 mb-3">
            {subjects.map(s => {
              const subjCounts = perSubjectCounts[s.name] || { total: 0, sections: {} };
              const ok = subjCounts.total >= s.quota;
              return (
                <Col key={s.name} sm={6} md={4} lg={3}>
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold">{s.name}</span>
                        <Badge bg={ok ? "success" : "warning"} text={ok ? undefined : "dark"}>
                          {subjCounts.total}/{s.quota}
                        </Badge>
                      </div>
                      <div className="d-flex flex-wrap gap-1">
                        {(s.sections || []).map(sec => {
                          const filled = (subjCounts.sections || {})[sec.key] || 0;
                          const quota = +sec.questionCount || 0;
                          const good = filled >= quota;
                          return (
                            <Badge key={sec.id} bg={good ? "light" : "warning"} text={good ? "dark" : undefined}>
                              {sec.key}: {filled}/{quota}
                            </Badge>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Filters (kept usable even if school is inactive) */}
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Subject</Form.Label>
                <Form.Select
                  value={activeSubject}
                  onChange={(e) => setActiveSubject(e.target.value)}
                >
                  {subjects.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Section</Form.Label>
                <Form.Select
                  value={filterSection}
                  onChange={(e) => setFilterSection(e.target.value)}
                >
                  <option value="">All</option>
                  {sectionOptions.map(s => (
                    <option key={s.key || "general"} value={s.key}>{s.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Filter</Form.Label>
                <Form.Control
                  placeholder="Search topic/text…"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Difficulty</Form.Label>
                <Form.Select
                  value={filterDifficulty}
                  onChange={(e) => setFilterDifficulty(e.target.value)}
                >
                  <option value="">All</option>
                  {DIFFICULTY.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={1} className="d-grid">
              <Button variant="outline-secondary" onClick={() => { setFilterText(""); setFilterDifficulty(""); setFilterSection(""); }}>
                Clear
              </Button>
            </Col>
          </Row>

          <hr />

          {/* Create / Edit form */}
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Section</Form.Label>
                <Form.Select
                  disabled={disabled}
                  value={sectionKey}
                  onChange={(e) => setSectionKey(e.target.value)}
                >
                  {sectionOptions.map(s => (
                    <option key={s.key || "general"} value={s.key}>{s.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {(isMixed || et === "objective") && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Question Type</Form.Label>
                  <Form.Select
                    disabled={disabled || et === "descriptive"}
                    value={questionType}
                    onChange={(e) => setQuestionType(e.target.value)}
                  >
                    {TYPE_OPTIONS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}

            <Col md={4}>
              <Form.Group>
                <Form.Label>Difficulty</Form.Label>
                <Form.Select
                  disabled={disabled}
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                >
                  {DIFFICULTY.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Topic (optional)</Form.Label>
                <Form.Control
                  disabled={disabled}
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., Kinematics"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Marks</Form.Label>
                <Form.Control
                  disabled={disabled}
                  type="number"
                  value={marks}
                  onChange={(e) => setMarks(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Negative</Form.Label>
                <Form.Control
                  type="number"
                  value={negativeMarks}
                  onChange={(e) => setNegativeMarks(e.target.value.replace(/[^-0-9.]/g, ""))}
                />
              </Form.Group>
            </Col>

            {questionType !== "case_based" && (
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Question</Form.Label>
                  <JoditEditor value={questionHtml} config={joditFullConfig} onBlur={(html) => setQuestionHtml(html)} />
                </Form.Group>
              </Col>
            )}

            {["mcq","multi_correct","mcq_descriptive"].includes(questionType) && (
              <>
                <Col md={12}>
                  <Form.Label>
                    {questionType === "multi_correct" ? "Options (multi correct)" : "Options (single correct)"}
                  </Form.Label>
                  <Row className="g-3">
                    {options.map((o, idx) => (
                      <Col md={6} key={o.key}>
                        <div className="d-flex align-items-start gap-2">
                          <div className="pt-2">
                            {questionType === "multi_correct" ? (
                              <Form.Check type="checkbox" checked={o.isCorrect} onChange={() => toggleCorrectMulti(idx)} />
                            ) : (
                              <Form.Check type="radio" name="correct" checked={o.isCorrect} onChange={() => setCorrectSingle(idx)} />
                            )}
                            <div className="text-center small text-muted mt-1">{o.key}</div>
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <JoditEditor
                              value={o.value}
                              config={joditMiniConfig}
                              onBlur={(html) => setOptions((curr) => curr.map((x,i)=> i===idx ? { ...x, value: html } : x))}
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>

                {questionType === "mcq_descriptive" && (
                  <Col md={12}>
                    <Form.Label>Descriptive Part</Form.Label>
                    <JoditEditor value={descPartHtml} config={{ ...joditFullConfig, height: 160 }} onBlur={(html) => setDescPartHtml(html)} />
                  </Col>
                )}
              </>
            )}

            {questionType === "true_false" && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Answer</Form.Label>
                  <Form.Select disabled={disabled} value={trueFalseAnswer} onChange={(e) => setTrueFalseAnswer(e.target.value)}>
                    <option>True</option>
                    <option>False</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            )}

            {questionType === "match" && (
              <Col md={12}>
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>No. of pairs (rows)</Form.Label>
                      <Form.Select
                        disabled={disabled}
                        value={matchCount}
                        onChange={(e) => setMatchCount(Number(e.target.value))}
                      >
                        {Array.from({ length: 19 }, (_, i) => i + 2).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md="auto">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => { setMatchOptions(buildMatchOptions(matchCount)); setCorrectMappingIdx(0); }}
                      disabled={disabled}
                    >
                      Regenerate Options
                    </Button>
                  </Col>
                  <Col className="text-muted small">
                    Type the full List-I / List-II table (with (a…)/(i…)) inside the Question editor above.
                  </Col>
                </Row>

                <Card className="mt-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-semibold">Answer Options</div>
                      <div className="text-muted small">Option 1 is sequential: (a-i), (b-ii), (c-iii)…</div>
                    </div>

                    {[0,1,2,3].map((idx) => (
                      <div key={idx} className="d-flex align-items-center gap-3 mb-2">
                        <Form.Check
                          type="radio"
                          name="matchCorrect"
                          checked={correctMappingIdx === idx}
                          onChange={() => setCorrectMappingIdx(idx)}
                          label={`Option ${idx + 1}`}
                          disabled={disabled}
                        />
                        <div className="small text-muted">{formatOptionText(matchOptions[idx])}</div>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            )}

            {questionType === "assertion_reason" && (
              <>
                <Col md={12}>
                  <Form.Label>Assertion</Form.Label>
                  <JoditEditor value={assertionHtml} config={joditFullConfig} onBlur={(html) => setAssertionHtml(html)} />
                </Col>
                <Col md={12}>
                  <Form.Label>Reason</Form.Label>
                  <JoditEditor value={reasonHtml} config={joditFullConfig} onBlur={(html) => setReasonHtml(html)} />
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Answer</Form.Label>
                    <Form.Select disabled={disabled} value={arAnswer} onChange={(e) => setArAnswer(e.target.value)}>
                      <option value="A">A: Both True, Reason is correct explanation</option>
                      <option value="B">B: Both True, Reason is NOT the explanation</option>
                      <option value="C">C: Assertion True, Reason False</option>
                      <option value="D">D: Assertion False</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </>
            )}

            {questionType === "statement_based" && (
              <Col md={12}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="m-0 fw-semibold">Statements</Form.Label>
                  <Button size="sm" variant="outline-primary" onClick={addStatement}>+ Add Statement</Button>
                </div>
                {statements.map((s, idx) => (
                  <Row className="g-2 align-items-start mb-3" key={s.id}>
                    <Col md={1}>
                      <Form.Group>
                        <Form.Label className="small">Key</Form.Label>
                        <Form.Control placeholder="A" value={s.key}
                          onChange={(e) => setStatements(curr => curr.map(x => x.id===s.id ? { ...x, key: e.target.value.toUpperCase().slice(0,1) } : x))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={9}>
                      <Form.Label className="small">Statement {idx+1}</Form.Label>
                      <JoditEditor value={s.html} config={{ ...joditFullConfig, height: 120 }}
                                   onBlur={(html) => setStatements(curr => curr.map(x => x.id===s.id ? { ...x, html } : x))} />
                    </Col>
                    <Col md={1} className="pt-4">
                      <Form.Check type="switch" label="True" checked={!!s.isTrue}
                                  onChange={() => setStatements(curr => curr.map(x => x.id===s.id ? { ...x, isTrue: !x.isTrue } : x))} />
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button variant="outline-danger" onClick={() => removeStatement(s.id)} disabled={statements.length<=1}>Remove</Button>
                    </Col>
                  </Row>
                ))}
                <Form.Text muted>Key is used in evaluation (e.g., A/B/C…). Mark which statements are true.</Form.Text>
              </Col>
            )}

            {questionType === "case_based" && (
              <>
                <Col md={12}>
                  <Form.Label>Passage / Case</Form.Label>
                  <JoditEditor value={passageHtml} config={{ ...joditFullConfig, height: 220 }} onBlur={(html) => setPassageHtml(html)} />
                </Col>

                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="m-0 fw-semibold">Sub-questions</Form.Label>
                    <Button size="sm" variant="outline-primary" onClick={addCaseSubQ}>+ Add Sub-question</Button>
                  </div>

                  {caseSubQs.map((sq, idx) => (
                    <Card className="mb-3" key={sq.id}>
                      <Card.Body>
                        <Row className="g-3">
                          <Col md={12}>
                            <Form.Label>Q{idx+1}</Form.Label>
                            <JoditEditor value={sq.questionHtml} config={{ ...joditFullConfig, height: 140 }}
                              onBlur={(html) => setCaseSubQs(arr => arr.map(x => x.id===sq.id ? { ...x, questionHtml: html } : x))}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Label>Options</Form.Label>
                            <Row className="g-3">
                              {sq.options.map((o, oIdx) => (
                                <Col md={6} key={o.key}>
                                  <div className="d-flex align-items-start gap-2">
                                    <div className="pt-2">
                                      <Form.Check type="radio" name={`caseCorrect-${sq.id}`} checked={sq.correctKey === o.key}
                                                  onChange={() => setCaseSubQs(arr => arr.map(x => x.id===sq.id ? { ...x, correctKey: o.key } : x))} />
                                      <div className="text-center small text-muted mt-1">{o.key}</div>
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                      <JoditEditor value={o.value} config={joditMiniConfig}
                                        onBlur={(html) => setCaseSubQs(arr => arr.map(x => {
                                          if (x.id !== sq.id) return x;
                                          const opts = x.options.map((oo, k) => k===oIdx ? { ...oo, value: html } : oo);
                                          return { ...x, options: opts };
                                        }))}
                                      />
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </Col>

                          <Col md={12} className="text-end">
                            {caseSubQs.length > 1 && (
                              <Button variant="outline-danger" size="sm" onClick={() => removeCaseSubQ(sq.id)}>
                                Remove sub-question
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </Col>
              </>
            )}

            <Col md={12}>
              <Form.Group>
                <Form.Label>Explanation (optional)</Form.Label>
                <JoditEditor value={explanationHtml} config={{ ...joditFullConfig, height: 160 }} onBlur={(html) => setExplanationHtml(html)} />
              </Form.Group>
            </Col>

            <Col md={12} className="d-flex gap-2">
              <Button onClick={() => setShowPreview(true)} variant="outline-secondary" disabled={disabled}>Preview</Button>
              <Button onClick={saveQuestion} disabled={disabled}>{editingId ? "Update Question" : "Add Question"}</Button>
              {editingId && (
                <Button variant="outline-secondary" onClick={hardReset} disabled={disabled}>Cancel Edit</Button>
              )}
            </Col>
          </Row>

          {/* Preview Modal (single) */}
          <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
            <Modal.Header closeButton><Modal.Title>Question Preview</Modal.Title></Modal.Header>
            <Modal.Body>
              <div className="mb-2">
                <Badge bg="light" text="dark">{activeSubject || "—"}</Badge>{" "}
                <Badge bg="light" text="dark">{difficulty}</Badge>{" "}
                <Badge bg="light" text="dark">{(questionType || "mcq").toUpperCase()}</Badge>{" "}
                <Badge bg="light" text="dark">{sectionKey ? `Sec ${sectionKey}` : "General"}</Badge>{" "}
                <Badge bg="light" text="dark">Marks: {marks}{Number(negativeMarks) ? ` / ${negativeMarks}` : ""}</Badge>
              </div>

              {questionType !== "case_based" ? (
                <div dangerouslySetInnerHTML={{ __html: questionHtml }} />
              ) : (
                <>
                  <div className="mb-3">
                    <small className="text-muted d-block">Passage</small>
                    <div dangerouslySetInnerHTML={{ __html: passageHtml }} />
                  </div>
                  {caseSubQs.map((sq, i) => (
                    <div key={sq.id} className="mb-3">
                      <small className="text-muted d-block">Sub-question {i+1}</small>
                      <div dangerouslySetInnerHTML={{ __html: sq.questionHtml }} />
                      <ol className="mt-2">
                        {sq.options.map(o => (
                          <li key={o.key}>
                            <div dangerouslySetInnerHTML={{ __html: o.value || "<em class='text-muted'>—</em>" }} />
                            {sq.correctKey === o.key && <Badge bg="success" className="ms-2">Correct</Badge>}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </>
              )}

              {["mcq","multi_correct","mcq_descriptive"].includes(questionType) && (
                <ol className="mt-3">
                  {options.map((o) => (
                    <li key={o.key}>
                      <div dangerouslySetInnerHTML={{ __html: o.value || "<em class='text-muted'>—</em>" }} />
                      {o.isCorrect && <Badge bg="success" className="ms-2">Correct</Badge>}
                    </li>
                  ))}
                </ol>
              )}

              {questionType === "true_false" && (
                <div className="mt-3"><Badge bg="info">Answer: {trueFalseAnswer}</Badge></div>
              )}

              {questionType === "match" && (
                <div className="mt-3">
                  <div className="mb-1 text-muted">Pairs: {matchCount} (labels auto: a… / i…)</div>
                  <div className="mb-2 fw-semibold">Answer Options</div>
                  {[0,1,2,3].map((idx) => (
                    <div key={idx} className="d-flex align-items-center gap-2 mb-1">
                      <Badge bg={correctMappingIdx===idx ? "success" : "secondary"}>Option {idx+1}</Badge>
                      <div className="text-muted small">{formatOptionText(matchOptions[idx])}</div>
                    </div>
                  ))}
                </div>
              )}

              {questionType === "assertion_reason" && (
                <div className="mt-3">
                  <p><strong>Assertion:</strong></p>
                  <div dangerouslySetInnerHTML={{ __html: assertionHtml }} />
                  <p className="mt-2"><strong>Reason:</strong></p>
                  <div dangerouslySetInnerHTML={{ __html: reasonHtml }} />
                  <p className="mt-2"><Badge bg="info">Answer: {arAnswer}</Badge></p>
                </div>
              )}

              {questionType === "statement_based" && (
                <div className="mt-3">
                  {statements.map((s, i) => (
                    <div key={s.id} className="mb-2">
                      <strong>{s.key || String.fromCharCode(65+i)}.</strong>{" "}
                      <span dangerouslySetInnerHTML={{ __html: s.html || "<em class='text-muted'>—</em>" }} />
                      {s.isTrue && <Badge bg="success" className="ms-2">True</Badge>}
                    </div>
                  ))}
                </div>
              )}

              {questionType === "mcq_descriptive" && (
                <>
                  <hr />
                  <small className="text-muted d-block">Descriptive Part</small>
                  <div className="text-muted" dangerouslySetInnerHTML={{ __html: descPartHtml }} />
                </>
              )}

              {explanationHtml && (
                <>
                  <hr />
                  <div className="text-muted" dangerouslySetInnerHTML={{ __html: explanationHtml }} />
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowPreview(false)}>Close</Button>
              <Button onClick={saveQuestion} disabled={disabled}>{editingId ? "Update" : "Add"}</Button>
            </Modal.Footer>
          </Modal>

          {/* Bulk Preview Modal (selected/filtered) */}
          <Modal show={showBulkPreview} onHide={() => setShowBulkPreview(false)} size="xl" centered>
            <Modal.Header closeButton>
              <Modal.Title>{previewTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
              {/* Use an iframe with srcDoc so images/styles render like they will when printing/Word-exporting */}
              <iframe
                title="bulk-preview"
                style={{ width: "100%", height: "70vh", border: "none" }}
                srcDoc={previewHtml}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => downloadAsWord(previewListRef.current)} disabled={!previewListRef.current.length}>
                Download Word (.doc)
              </Button>
              <Button variant="primary" onClick={() => openPrintView(previewListRef.current)} disabled={!previewListRef.current.length}>
                Print / PDF
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Missing breakdown modal */}
          <Modal show={showMissingModal} onHide={() => setShowMissingModal(false)} centered>
            <Modal.Header closeButton><Modal.Title>Missing Questions</Modal.Title></Modal.Header>
            <Modal.Body>
              {completion.missing.length === 0 ? (
                <div className="text-success">All sections are filled. You can mark the template as <strong>Ready</strong>.</div>
              ) : (
                <div className="small">
                  <p className="mb-2 text-muted">Add the following to enable <strong>Mark Ready</strong>:</p>
                  <ul className="mb-0">
                    {completion.missing.map((m, i) => (
                      <li key={i}><strong>{m.subject}</strong> — {m.label} : need {m.need}, have {m.have} (<strong>{m.short} more</strong>)</li>
                    ))}
                  </ul>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowMissingModal(false)}>Close</Button>
            </Modal.Footer>
          </Modal>

          <hr />

          {/* Bulk upload */}
       {/* Bulk upload */}
<Card className="border-0 shadow-sm mb-4">
  <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
    <div className="d-flex align-items-center gap-2">
      <span className="fw-semibold">Bulk Upload</span>
      <Badge bg="light" text="dark">CSV / Excel</Badge>
    </div>

    <div className="d-flex align-items-center gap-2">
      <Form.Group className="m-0">
        <Form.Select
          size="sm"
          value={bulkType}
          onChange={(e) => setBulkType(e.target.value)}
          disabled={disabled}
          style={{ minWidth: 260 }}
        >
          <option value="auto">Auto (use questionType column)</option>
          <option value="mcq">MCQ (Single Correct)</option>
          <option value="multi_correct">MCQ (Multi Correct)</option>
          <option value="mcq_descriptive">MCQ + Descriptive</option>
          <option value="true_false">True / False</option>
          <option value="descriptive">Descriptive</option>
          <option value="match">Match the Following</option>
          <option value="assertion_reason">Assertion &amp; Reason</option>
          <option value="statement_based">Statement Based</option>
          <option value="case_based">Case Based</option>
        </Form.Select>
      </Form.Group>

      <Button
        size="sm"
        variant="outline-primary"
        onClick={downloadCsvTemplate}
        disabled={disabled}
        title="Get a ready-to-fill CSV tailored to the selected type"
      >
        Download CSV Template
      </Button>
    </div>
  </Card.Header>

  <Card.Body>
    <Row className="g-3">
      <Col lg={8}>
        {/* Drag & Drop Zone + file picker */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer?.files?.[0];
            if (f) onAnyFile(f);
          }}
          className={`rounded-3 p-4 text-center border ${dragOver ? "border-primary bg-light" : "border-2 bg-body"}`}
          style={{ borderStyle: "dashed", cursor: disabled ? "not-allowed" : "pointer" }}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <div className="fw-semibold mb-1">Drop your CSV / Excel here</div>
          <div className="text-muted small mb-3">
            or <u>click to choose a file</u> (accepted: .csv, .xlsx, .xls)
          </div>

          <div className="d-flex justify-content-center">
            <Button variant="outline-secondary" size="sm" disabled={disabled}>
              Choose File
            </Button>
          </div>

          {selectedFile && (
            <div className="mt-3 small">
              <Badge bg="light" text="dark" className="me-2">Selected</Badge>
              <span className="text-muted">{selectedFile.name}</span>
              <Button
                size="sm"
                variant="link"
                className="ms-2 p-0 align-baseline"
                onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              >
                Clear
              </Button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            hidden
            disabled={disabled}
            onChange={(e) => e.target.files?.[0] && onAnyFile(e.target.files[0])}
          />
        </div>

        <div className="mt-2 text-muted small">
          Put HTML (including images) directly in <code>*Html</code> columns (e.g., <code>questionHtml</code>, <code>passageHtml</code>).
          For images, use URLs or <code>&lt;img src="data:...base64"&gt;</code>.
        </div>
      </Col>

      <Col lg={4}>
        <Card bg="light" className="h-100">
          <Card.Body className="small">
            <div className="fw-semibold mb-2">Tips</div>
            <ul className="m-0 ps-3">
              <li className="mb-1">
                If <code>questionType</code> column is present, it overrides the “Upload for type” selector.
              </li>
              <li className="mb-1">
                Keep subject names exact; unknown subjects will be skipped with an error.
              </li>
              <li>
                Download the template to see the exact columns for each type.
              </li>
            </ul>
          </Card.Body>
        </Card>
      </Col>

      {bulkErrors.length > 0 && (
        <Col md={12}>
          <Alert variant="danger" className="mb-0">
            <div className="fw-semibold mb-1">Bulk upload errors</div>
            <ul className="mb-0">{bulkErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </Alert>
        </Col>
      )}
    </Row>
  </Card.Body>
</Card>

          <hr />

          {/* Questions table with multi-select, bulk delete & preview */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column">
                <span className="fw-semibold">Questions ({filteredQuestions.length})</span>
                {selectedIds.size > 0 && (
                  <div className="mt-1 d-flex align-items-center gap-2">
                    <Badge bg="primary">{selectedIds.size} selected</Badge>
                    <Button size="sm" variant="outline-danger" onClick={deleteSelected}>Delete Selected</Button>
                    <Button size="sm" variant="outline-secondary" onClick={openPreviewSelected}>Preview Selected</Button>
                    <Button size="sm" variant="outline-secondary" onClick={openPreviewFiltered}>Preview All (Filtered)</Button>
                  </div>
                )}
              </div>

              <div className="text-muted small">
                Showing {activeSubject || "All"}
                {filterSection ? <> · Section {filterSection}</> : null}
                {filterDifficulty && <> · {filterDifficulty}</>}
                {selectedIds.size === 0 && (
                  <div className="mt-1 d-flex justify-content-end">
                    <Button size="sm" variant="outline-secondary" onClick={openPreviewFiltered} disabled={filteredQuestions.length === 0}>
                      Preview All (Filtered)
                    </Button>
                  </div>
                )}
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {qLoading ? (
                <div className="p-4 text-center"><Spinner /></div>
              ) : filteredQuestions.length === 0 ? (
                <div className="p-4 text-muted">No questions yet.</div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <Form.Check
                          type="checkbox"
                          checked={filteredQuestions.length > 0 && filteredQuestions.every(q => selectedIds.has(q.id))}
                          onChange={toggleAllFiltered}
                          title="Select all (filtered)"
                        />
                      </th>
                      <th>Subject</th>
                      <th>Section</th>
                      <th>Topic</th>
                      <th>Type</th>
                      <th>Difficulty</th>
                      <th>Marks</th>
                      <th>Preview</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuestions.map(q => (
                      <tr key={q.id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.has(q.id)}
                            onChange={() => toggleRow(q.id)}
                            aria-label="Select row"
                          />
                        </td>
                        <td>{q.subject}</td>
                        <td style={{ minWidth: 160 }}>
  <Form.Select
    size="sm"
    disabled={disabled || savingSectionIds.has(q.id)}
    value={q.sectionKey || ""}
    onChange={(e) => updateQuestionSection(q, e.target.value)}
  >
    <option value="">General</option>
    {(sectionOptionsBySubject[q.subject] || []).map(o => (
      <option key={o.key || "general"} value={o.key}>{o.label}</option>
    ))}
  </Form.Select>
</td>
                        <td>{q.topic || "—"}</td>
                        <td><Badge bg="light" text="dark">{(q.questionType || "mcq").toUpperCase()}</Badge></td>
                        <td><Badge bg="light" text="dark">{q.difficulty}</Badge></td>
                        <td>{q.marks}{q.negativeMarks ? <span className="text-muted">{` / ${q.negativeMarks}`}</span> : null}</td>
                        <td>
                          <div style={{ maxWidth: 380, maxHeight: 64, overflow: "hidden" }}
                               dangerouslySetInnerHTML={{ __html: q.questionHtml || q.passageHtml || "" }} />
                        </td>
                        <td className="text-end">
                          <Dropdown as={ButtonGroup}>
                            <Button size="sm" variant="outline-primary" onClick={() => startEdit(q)}>Edit</Button>
                            <Dropdown.Toggle split size="sm" variant="outline-primary" />
                            <Dropdown.Menu align="end">
                              <Dropdown.Item onClick={() => startEdit(q)}>Edit</Dropdown.Item>
                              <Dropdown.Item onClick={() => { setShowPreview(true); startEdit(q); }}>Quick Preview</Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item className="text-danger" onClick={() => deleteQuestion(q)}>Delete</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </div>
  );
}
